import React from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import type { SVGProps } from 'react';
import type { FC } from 'react';

export const iconTypes = {
  ALERT_ICON: 'ALERT_ICON',
  ARROW: 'ARROW',
  ARROW_BACK_ICON: 'ARROW_BACK_ICON',
  BUILD_AREA_ICON : 'BUILD_AREA_ICON',
  BUILD_AREA_ICON_GRAY: 'BUILD_AREA_ICON_GRAY',
  CUBE_ICON: 'CUBE_ICON',
  QUOTES: 'QUOTES',
  LOGO: 'LOGO',
  SIGN_IN: 'SIGN_IN',
  USER_ICON: 'USER_ICON',
  FACEBOOK_ICON: 'FACEBOOK_ICON',
  TWITTER_ICON: 'TWITTER_ICON',
  INSTAGRAM_ICON: 'INSTAGRAM_ICON',
  INVEST_ICON_GRAY: 'INVEST_ICON_GRAY',
  WHATSAPP_ICON: 'WHATSAPP_ICON',
  PHONE_ICON: 'PHONE_ICON',
  LINKEDIN_ICON: 'LINKEDIN_ICON',
  INVEST_ICON: 'INVEST_ICON',
  TROPHY_ICON: 'TROPHY_ICON',
  SUSTAINABILITY_ICON: 'SUSTAINABILITY_ICON',
  MODULAR_CONSTRUCTION_ICON: 'MODULAR_CONSTRUCTION_ICON',
  VIRTUAL_REALITY_ICON: 'VIRTUAL_REALITY_ICON',
  SPIRAL_ICON: 'SPIRAL_ICON',
  DINOSAURS_ICON: 'DINOSAURS_ICON',
  APARTMENTS_ICON: 'APARTMENTS_ICON',
  ARCHITECTONIC_ICON: 'ARCHITECTONIC_ICON',
  RENEWABLE_ENERGY_ICON: 'RENEWABLE_ENERGY_ICON',
  CERTSIGN_ICON: 'CERTSIGN_ICON',
  CERTSIGN_ICON_GRAY: 'CERTSIGN_ICON_GRAY',
  HELIPORT_ICON: 'HELIPORT_ICON',
  ACCESSIBILITY_ICON: 'ACCESSIBILITY_ICON',
  SOLAR_PANEL_ICON: 'SOLAR_PANEL_ICON',
  OPEN_24HOURS_ICON: 'OPEN_24HOURS_ICON',
  BR: 'BR',
  ES: 'ES',
  US: 'US'
} as const;

export type IconType = keyof typeof iconTypes;

export interface SVGIconsProps {
  type: IconType;
  color?: string;
  fill?: string;
  width: number | string;
  height: number | string;
  onClick?: () => void;
  rotate?: number;
}

const Svg = styled.svg<any>`
    transform: rotate(${({ rotate }) => rotate || 0}deg);
`;

const Span = styled.span<any>`
    display: flex;
    align-items: center;
    cursor: unset;
`;

const iconMap: Record<IconType, () => Promise<{ default: FC<SVGProps<SVGSVGElement>> }>> = {
  ALERT_ICON: () => import('./SVGIcons/AlertIcon'),
  ARROW: () => import('./SVGIcons/ArrowIcon'),
  ARROW_BACK_ICON: () => import('./SVGIcons/ArrowBack'),
  BUILD_AREA_ICON: () => import('./SVGIcons/BuildAreaIcon'),
  BUILD_AREA_ICON_GRAY: () => import('./SVGIcons/BuildAreaIconGray'),
  CUBE_ICON: () => import('./SVGIcons/CubeIcon'),
  QUOTES: () => import('./SVGIcons/QuoteIcon'),
  LOGO: () => import('./SVGIcons/LogoIcon'),
  SIGN_IN: () => import('./SVGIcons/SignInIcon'),
  USER_ICON: () => import('./SVGIcons/UserIcon'),
  FACEBOOK_ICON: () => import('./SVGIcons/FacebookIcon'),
  TWITTER_ICON: () => import('./SVGIcons/TwitterIcon'),
  INSTAGRAM_ICON: () => import('./SVGIcons/InstagramIcon'),
  INVEST_ICON_GRAY: () => import('./SVGIcons/InvestIconGray'),
  WHATSAPP_ICON: () => import('./SVGIcons/WhatsappIcon'),
  PHONE_ICON: () => import('./SVGIcons/PhoneIcon'),
  LINKEDIN_ICON: () => import('./SVGIcons/LinkedInIcon'),
  INVEST_ICON: () => import('./SVGIcons/InvestIcon'),
  TROPHY_ICON: () => import('./SVGIcons/TrophyIcon'),
  SUSTAINABILITY_ICON: () => import('./SVGIcons/SustainabilityIcon'),
  MODULAR_CONSTRUCTION_ICON: () => import('./SVGIcons/ModularConstructionIcon'),
  VIRTUAL_REALITY_ICON: () => import('./SVGIcons/VirtualRealityIcon'),
  SPIRAL_ICON: () => import('./SVGIcons/SpiralIcon'),
  DINOSAURS_ICON: () => import('./SVGIcons/DinosaursIcon'),
  APARTMENTS_ICON: () => import('./SVGIcons/ApartmentsIcon'),
  ARCHITECTONIC_ICON: () => import('./SVGIcons/ArchitectonicIcon'),
  RENEWABLE_ENERGY_ICON: () => import('./SVGIcons/RenewableEnergyIcon'),
  CERTSIGN_ICON: () => import('./SVGIcons/CertsignIcon'),
  CERTSIGN_ICON_GRAY: () => import('./SVGIcons/CertsignIconGray'),
  HELIPORT_ICON: () => import('./SVGIcons/HeliportIcon'),
  ACCESSIBILITY_ICON: () => import('./SVGIcons/AccessibilityIcon'),
  SOLAR_PANEL_ICON: () => import('./SVGIcons/SolarPanelIcon'),
  OPEN_24HOURS_ICON: () => import('./SVGIcons/Open24HoursIcon'),
  BR: () => import('./SVGIcons/BR'),
  ES: () => import('./SVGIcons/ES'),
  US: () => import('./SVGIcons/US'),
};

const IconContainer = styled.span<{ rotate?: number }>`
  display: inline-flex;
  align-items: center;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  transform: ${({ rotate }) => (rotate !== undefined ? `rotate(${rotate}deg)` : 'none')};
  transition: transform 0.2s ease;
`;

const SVGIcons: React.FC<SVGIconsProps> = ({
  type,
  width,
  height,
  rotate,
  color,
  onClick,
  ...props
}) => {
  const DynamicIcon = dynamic(iconMap[type], {
    loading: () => <span style={{ width, height }} />,
    ssr: false,
  });

  return (
    <IconContainer
      onClick={onClick || undefined}
      rotate={rotate}
      style={{ width, height }}
    >
      <DynamicIcon
        width={width}
        height={height}
        fill={color}
        {...props}
      />
    </IconContainer>
  );
};

export default SVGIcons;